import { createViewContext } from "../../Components/createViewContext"
import { RouterNavigateFunction } from "../../App/Routes"
import { UseOverlaySetValue, UseOverlayResetValue } from "../../App/useOverlay"
import { logger } from "../../App/Logger"
import { subscriptionExpired } from "../../App/IAP"
import { Platform } from "../../App/Platform"
import { ApiClient, IAccount, IAccountUserLevel, ILogbookAppStatusResult, ILogbookSyncStatus, IShip, IShipUser, IShipUserLevel, ShipApi } from "../../Types"
import { ApiUser } from "../../Types/Generated/LogbookApi/ApiRuntime"
import { AppOverlayContent, AppOverlayContentOptions } from "./OverlayContentView"
import { AppRoute } from "../../App/Routes"

export const AppViewData = {
  loadShips(api: ApiClient) {
    return new ShipApi(api).ships.list({ params: {} }).call();
  },
  closeShip(api: ApiClient, shipId: number) {
    return new ShipApi(api).ships.select({ params: { shipId }, body: { close: true } }).call();
  },
  loadShip(api: ApiClient, shipId: number) {
    return new ShipApi(api).ships.get({ params: { shipId } }).call();
  },
  // The app's live sync status, or `undefined` when it could not be read. Used by
  // the ship-load retry to tell "the app is still populating the local DB" from "the
  // ship really will not load" -- a distinction only the app itself can make, and one
  // the failure it answers with (503) does not carry.
  //
  // Read live rather than from `app.status`: selecting a ship restarts the app
  // instance, so the socket that pushes status broadcasts is commonly reconnecting
  // during exactly this window, leaving the cached snapshot stale or absent. The
  // status route is ungated (`Routes.swift`) and answers throughout. A failure is
  // absorbed: an unreadable status must never be mistaken for "still syncing", which
  // would wait forever.
  async syncStatus(app: { updateStatus: () => Promise<ILogbookAppStatusResult> }): Promise<ILogbookSyncStatus | undefined> {
    try {
      return (await app.updateStatus())?.sync?.status;
    } catch (error) {
      logger.debug("sync status read failed", error);
      return undefined;
    }
  },
  // Human-readable device label for display (Settings/About). Delegates platform
  // detection to the single App/Platform helper; cloud/browser sessions have no
  // native device label.
  platform(cloudSession: boolean, appStyles: string[]): string | undefined {
    if (cloudSession) {
      return undefined;
    }
    switch (Platform.of({ appStyles })) {
      case "windows": return "Windows";
      case "mac": return "macOS";
      case "ios": return "iPad";
      case "android": return "Android";
      default: return undefined;
    }
  },
  requireUserLevel(level: string, shipUser?: IShipUser): boolean {
    const requiredLevel = IShipUserLevel[level as keyof typeof IShipUserLevel];
    if (requiredLevel === undefined) {
      logger.error("Unknown user level " + level);
      return false;
    }
    if (!shipUser) {
      return requiredLevel <= IShipUserLevel.none;
    }
    return shipUser.shipUserLevel >= requiredLevel;
  },
  subscriptionExpired(account?: IAccount): boolean {
    return subscriptionExpired(account);
  },
  // Builds the ApiUser the generated `<Api>.<method>.available(user)` predicates check
  // against. `shipUser` is the currently open ship's crew record (freshest data, keyed by
  // its own `shipId` — NOT `app.shipId`, which is a local/native device binding that a cloud
  // session never populates, e.g. navigating straight to a ship URL); `membership` is the
  // per-ship level list from the ships-list response, used for any OTHER ship id (e.g.
  // account-level ship selector actions). `accountId`/`accountUserLevel` are the current user's
  // own account and their `accountUserLevel` (only ever populated by a cloud session — see
  // SessionResponse.accountUserLevel).
  apiUser(params: {
    accountId?: number;
    accountUserLevel?: IAccountUserLevel;
    shipUser?: IShipUser;
    membership?: IShipUser[];
  }): ApiUser {
    return {
      accountLevel(accountId?: number): IAccountUserLevel | undefined {
        if (accountId != null && accountId !== params.accountId) {
          return undefined;
        }
        return params.accountUserLevel;
      },
      shipLevel(shipId?: number): IShipUserLevel | undefined {
        const targetShipId = shipId ?? params.shipUser?.shipId;
        if (targetShipId == null) {
          return undefined;
        }
        if (targetShipId === params.shipUser?.shipId) {
          return params.shipUser?.shipUserLevel;
        }
        return params.membership?.find((m) => m.shipId === targetShipId)?.shipUserLevel;
      },
    };
  },
  // Combines a route's baseline `.available()` (editCurrent) with the `editAll`
  // escalation the server applies once a record's trip is historical — mirrors the
  // server's own two-step "baseline gate, then editAll if not current" pattern (e.g.
  // DayApi.update, NoteApi's attachment routes). See finding 4 / Phase F, #931: the
  // generated `.available()` alone only ever reflects the baseline editCurrent check.
  availableCurrentOrAll(apiUser: ApiUser | undefined, shipId: number, isCurrentTrip: boolean): boolean {
    const requiredLevel = isCurrentTrip ? IShipUserLevel.editCurrent : IShipUserLevel.editAll;
    return (apiUser?.shipLevel(shipId) ?? -1) >= requiredLevel;
  },
};

export interface AppViewState {
  connected: boolean;
  loadShips: (force?: boolean) => Promise<void>;
  navigateApp: RouterNavigateFunction;
  route: AppRoute;
  routeName: string;
  ships?: IShip[];
  // The ship this device holds the primary claim on (`ShipsResponse.ownShipId`),
  // undefined in a cloud session or once the claim is released (#1279).
  ownShipId?: number;
  membership?: IShipUser[];
  subscriptionExpired: boolean;
  deselectShip: (shipId: number) => Promise<void>;
  selectShip: (shipId: number) => Promise<void>;
  resetShip: () => void;
  forceCloseShip: () => void;
  // Whether a ship close is in flight, and whether the last one failed (#1279).
  // The close makes a cloud round trip, so the confirm dialog shows progress,
  // refuses a second submit meanwhile, and reports a failure rather than
  // appearing to do nothing.
  shipClosing?: boolean;
  shipCloseFailed?: boolean;
  /// HTTP status of the failed close, when known, so the confirm body can name the
  /// cause rather than blaming the internet whatever happened (#1453).
  shipCloseFailedStatus?: number;
  appOverlay: AppOverlayContent;
  appOverlayOptions: AppOverlayContentOptions;
  setAppOverlay: UseOverlaySetValue<AppOverlayContent, AppOverlayContentOptions>;
  resetAppOverlay: UseOverlayResetValue;
  // The ShipUser new attributed entries (log/note) are written as, when acting as someone
  // other than the real, authenticated ShipUser (#197/#1026 (tracker #827) user switch). Owned here
  // (not ShipViewContext) because MenuAccount -- which triggers the picker -- sits outside
  // ShipViewContext's subtree (only ShipView.tsx's own return provides it); AppView passes
  // it down to ShipView as a prop for the picker overlay's own UI. Setting it also updates
  // Client's static acting-ship-user header so every subsequent API call attributes new
  // entries accordingly; it never changes `apiUser`/`requireUserLevel`, which always keep
  // resolving from the real `shipUser` -- the permission ceiling is structural, not
  // enforced here.
  actingShipUser?: IShipUser;
  setActingShipUser: (user?: IShipUser) => void;
}

const appViewContext = createViewContext<AppViewState>();
export const AppViewContext = appViewContext.Context;
export const GetAppView = appViewContext.useGet;
